/**
 * SCRUM-1032 QC item 7 — how many stations still have nowhere on the map.
 *
 * The Finish dialog used to work this out itself, counting rows whose
 * `grsCoordinatedLatitude` was not a `number`. `/api/station` does not return
 * that field at all in its list response, so the test was true for every row
 * and the dialog reported the total: "15 stations have no location pinned" on a
 * controller with 15 stations, two of them pinned.
 *
 * The endpoint answers the question directly with `unpinnedStations`, so that
 * is what is used. The row count survives only as a fallback for a response
 * that omits it, and now tests the value rather than its type — a coordinate
 * arriving as a string is still a pinned station.
 */

export interface UnpinnedStationSource {
  /** The server's own count. Authoritative when present. */
  unpinnedStations?: unknown
  /** The page of stations, when the server did not answer with a count. */
  list?: unknown
}

const isPinned = (station: any) =>
  station?.grsCoordinatedLatitude != null &&
  station?.grsCoordinatedLongitude != null

export const unpinnedStationCount = (
  data: UnpinnedStationSource | undefined,
): number => {
  const fromServer = data?.unpinnedStations
  // `typeof`, not a truthiness check: zero unpinned stations is an answer, and
  // the reason to say nothing at all rather than fall through to the rows.
  if (typeof fromServer === 'number') return fromServer

  const list = data?.list
  // Some list hooks answer an empty page with `false` rather than an array.
  if (!Array.isArray(list)) return 0
  return list.filter((station) => !isPinned(station)).length
}

export default unpinnedStationCount
